/* =========================================================
   UNISOURCE DESIGN INSPIRATION → DESIGNER MODAL BRIDGE

   Purpose:
   - Prevent Design Inspiration "Design Online & Order" links
     from navigating directly to InkSoft in the same tab/page.
   - Open the existing unified InkSoft Designer modal instead.
   - Preserve the exact currently selected:
       Product ID
       Product Style ID / Color
       Design ID
   - Rebuild a canonical InkSoft share URL:
       /shop/design-studio/share/{productId}/{styleId}/{designId}

   Important:
   - Current data-* attributes take priority over values found
     in a potentially stale share URL.
   - Existing URL values remain available as fallbacks.
========================================================= */

(function () {
  'use strict';

  var DEBUG = true;

  var INKSOFT_ORIGIN = 'https://shop.unisourceapparel.com';
  var INKSOFT_STORE_PATH = '/Unisource_Apparel';

  function asString(value) {
    return value == null ? '' : String(value).trim();
  }

  function safeDecodeURIComponent(value) {
    var cleaned = asString(value);

    if (!cleaned) return '';

    try {
      return decodeURIComponent(cleaned);
    } catch (_error) {
      return cleaned;
    }
  }

  function normalizeInksoftUrl(url) {
    var value = asString(url);

    if (!value) return '';

    /*
     * Remove an accidental duplicate storefront path without
     * changing any other portion of the URL.
     */
    value = value.replace(
      /\/Unisource_Apparel\/Unisource_Apparel\//g,
      '/Unisource_Apparel/'
    );

    if (/^https?:\/\//i.test(value)) {
      return value;
    }

    if (value.indexOf('/Unisource_Apparel/') === 0) {
      return INKSOFT_ORIGIN + value;
    }

    if (value.indexOf('/shop/') === 0) {
      return INKSOFT_ORIGIN + INKSOFT_STORE_PATH + value;
    }

    return value;
  }

  function isInksoftDesignerUrl(url) {
    var value = asString(url);

    return (
      value.indexOf('/shop/design-studio/share/') !== -1 ||
      value.indexOf('/shop/design-studio/design') !== -1 ||
      value.indexOf('/pages/design-studio') !== -1
    );
  }

  function extractShareParts(url) {
    var value = asString(url);

    var match = value.match(
      /\/shop\/design-studio\/share\/([^/?#]+)\/([^/?#]+)(?:\/([^/?#]+))?/i
    );

    if (!match) {
      return {
        productId: '',
        styleId: '',
        designId: ''
      };
    }

    return {
      productId: safeDecodeURIComponent(match[1]),
      styleId: safeDecodeURIComponent(match[2]),
      designId: safeDecodeURIComponent(match[3])
    };
  }

  function getQueryValue(url, keys) {
    var parsed;

    try {
      parsed = new URL(url, window.location.origin);
    } catch (_error) {
      return '';
    }

    for (var i = 0; i < keys.length; i += 1) {
      var value = asString(parsed.searchParams.get(keys[i]));

      if (value) {
        return value;
      }
    }

    return '';
  }

  function getAttributeValue(el, attributeNames) {
    if (!el || !attributeNames || !attributeNames.length) {
      return '';
    }

    for (var i = 0; i < attributeNames.length; i += 1) {
      var value = asString(el.getAttribute(attributeNames[i]));

      if (value) {
        return value;
      }
    }

    return '';
  }

  function getLaunchUrlFromElement(el) {
    if (!el) return '';

    var href = '';

    if (el.tagName && el.tagName.toLowerCase() === 'a') {
      href = asString(el.getAttribute('href'));
    }

    return normalizeInksoftUrl(
      href ||
      el.getAttribute('data-launch-url') ||
      el.getAttribute('data-share-url') ||
      el.getAttribute('data-designer-url') ||
      el.getAttribute('data-url') ||
      ''
    );
  }

  function buildCanonicalShareUrl(productId, styleId, designId, fallbackUrl) {
    var cleanProductId = asString(productId);
    var cleanStyleId = asString(styleId);
    var cleanDesignId = asString(designId);

    /*
     * Product ID and Style ID are both required for an exact
     * apparel/color launch. If either is unavailable, preserve
     * the existing URL rather than constructing an incomplete URL.
     */
    if (!cleanProductId || !cleanStyleId) {
      return normalizeInksoftUrl(fallbackUrl);
    }

    var canonicalUrl =
      INKSOFT_ORIGIN +
      INKSOFT_STORE_PATH +
      '/shop/design-studio/share/' +
      encodeURIComponent(cleanProductId) +
      '/' +
      encodeURIComponent(cleanStyleId);

    if (cleanDesignId) {
      canonicalUrl += '/' + encodeURIComponent(cleanDesignId);
    }

    return canonicalUrl;
  }

  function getLaunchConfig(el, originalLaunchUrl) {
    var launchUrl = normalizeInksoftUrl(originalLaunchUrl);
    var parts = extractShareParts(launchUrl);

    /*
     * Priority order:
     *
     * 1. Current data-* value on the clicked element
     * 2. Value extracted from the share URL path
     * 3. Value found in URL query parameters
     *
     * This prevents an older or stale share URL from overriding
     * the apparel reference currently selected in the Visualizer.
     */
    var productId =
      getAttributeValue(el, [
        'data-product-id',
        'data-productid',
        'data-inksoft-product-id',
        'data-embedded-product-id'
      ]) ||
      parts.productId ||
      getQueryValue(launchUrl, [
        'productId',
        'productID',
        'embeddedProductId',
        'id'
      ]);

    var styleId =
      getAttributeValue(el, [
        'data-style-id',
        'data-styleid',
        'data-product-style-id',
        'data-productstyleid',
        'data-inksoft-style-id',
        'data-selected-style-id'
      ]) ||
      parts.styleId ||
      getQueryValue(launchUrl, [
        'styleId',
        'styleID',
        'productStyleId',
        'productStyleID',
        'selectedStyleId'
      ]);

    var designId =
      getAttributeValue(el, [
        'data-design-id',
        'data-designid',
        'data-artwork-id',
        'data-artworkid',
        'data-template-design-id',
        'data-inksoft-design-id'
      ]) ||
      parts.designId ||
      getQueryValue(launchUrl, [
        'designId',
        'designID',
        'artworkId',
        'artworkID',
        'templateDesignId'
      ]);

    var colorName =
      getAttributeValue(el, [
        'data-color-name',
        'data-selected-color',
        'data-color',
        'data-style-name'
      ]) ||
      getQueryValue(launchUrl, [
        'colorName',
        'selectedColor',
        'color',
        'styleName'
      ]);

    var apparelKey =
      getAttributeValue(el, [
        'data-apparel-key',
        'data-selected-apparel-key'
      ]);

    /*
     * Rebuild the final share URL from the resolved current IDs.
     * If the current element does not provide IDs, the original
     * URL path values safely remain in use.
     */
    var canonicalShareUrl = buildCanonicalShareUrl(
      productId,
      styleId,
      designId,
      launchUrl
    );

    return {
      productId: productId,
      styleId: styleId,
      productStyleId: styleId,

      designId: designId,
      artworkId: designId,
      templateDesignId: designId,

      colorName: colorName,
      selectedColor: colorName,
      apparelKey: apparelKey,

      launchUrl: canonicalShareUrl,
      shareUrl: canonicalShareUrl,
      selectedShareUrl: canonicalShareUrl,

      /*
       * Preserve the original URL for diagnostics only.
       * Existing embed code can ignore this property.
       */
      originalLaunchUrl: launchUrl,

      wrapperUrl: ''
    };
  }

  function shouldHandleClick(target) {
    var el =
      target && target.closest
        ? target.closest(
            'a, button, [data-launch-url], [data-share-url], [data-designer-url]'
          )
        : null;

    if (!el) return null;

    var launchUrl = getLaunchUrlFromElement(el);
    var text = asString(el.textContent).toLowerCase();

    var looksLikeDesignerButton =
      text.indexOf('design online') !== -1 ||
      text.indexOf('design online & order') !== -1 ||
      el.hasAttribute('data-design-online') ||
      el.hasAttribute('data-inksoft-designer-launch');

    if (!isInksoftDesignerUrl(launchUrl) && !looksLikeDesignerButton) {
      return null;
    }

    /*
     * Do not intercept a button unless it has an actual supported
     * Designer URL. This preserves any unrelated button behavior.
     */
    if (!launchUrl || !isInksoftDesignerUrl(launchUrl)) {
      return null;
    }

    return {
      element: el,
      launchUrl: launchUrl
    };
  }

  function openDesignerModal(config) {
    if (
      window.InksoftPOC &&
      window.InksoftPOC.designerEmbed &&
      typeof window.InksoftPOC.designerEmbed.open === 'function'
    ) {
      window.InksoftPOC.designerEmbed.open(config);
      return true;
    }

    return false;
  }

  document.addEventListener(
    'click',
    function (event) {
      var match = shouldHandleClick(event.target);

      if (!match) return;

      var config = getLaunchConfig(
        match.element,
        match.launchUrl
      );

      event.preventDefault();
      event.stopPropagation();

      if (DEBUG && window.console) {
        console.log(
          '[Unisource Design Inspiration Modal Bridge] Opening Designer modal',
          {
            productId: config.productId,
            styleId: config.styleId,
            designId: config.designId,
            colorName: config.colorName,
            apparelKey: config.apparelKey,
            originalLaunchUrl: config.originalLaunchUrl,
            finalLaunchUrl: config.launchUrl
          }
        );
      }

      if (!openDesignerModal(config)) {
        if (window.console) {
          console.warn(
            '[Unisource Design Inspiration Modal Bridge] designerEmbed module not found. Falling back to direct navigation.',
            config
          );
        }

        window.location.href = config.launchUrl || match.launchUrl;
      }
    },
    true
  );
})();